import { useState, useRef, useEffect, useCallback, useContext } from "react"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import MainMetrics from "../components/MainMetrics"
import CompanyInformation from "../components/CompanyInformation"
import FinancialDataSummary from "../components/FinancialDataSummary"
import SummaryMarketGraph from "../components/SummaryMarketGraph"
import SelectColumnContainer from "../components/SelectColumnContainer"
import SaveSettingButton from "../components/SaveSettingButton"
import SummaryGraph from "../components/SummaryGraph"
import { UserSettingsContext, getDescriptionBySettingName } from "../../../context/UserSettingsContext"
import { useDashboardContext } from "../../../context/DashboardContext"
import {
  fetchMeticsList,
  fetchLatestDividendData,
  fetchStockData,
  fetchDividendData,
  fetchFinancialResults,
  fetchQuarterlyFinancialData,
} from "../../api/api_calls"
import Loading from "../../Loading"
import ROICTreeVisualizer from "../../utils/roic-tree-visualizer"


const SummaryTab = ({ secCode, companyInfo }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams() 
  const { state: dashboardState, dispatch: dashboardDispatch } = useDashboardContext()
  const { userSettings, addUserSetting, selectableAccounts } = useContext(UserSettingsContext)

  const [isTwoColumns, setIsTwoColumns] = useState(true)
  const [financialData, setFinancialData] = useState(null)
  // const [companyInfo, setCompanyInfo] = useState(null)
  const [latestStockPrice, setLatestStockPrice] = useState({})
  const [latestDividendData, setLatestDividendData] = useState(null)
  const [dividendData, setDividendData] = useState(null)
  const [latestFinancialData, setLatestFinancialData] = useState(null)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [accountingStandard, setAccountingStandard] = useState(null)
  const [metricsList, setMetricsList] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [graphLoaded, setGraphLoaded] = useState(false)
  const [defaultAccount, setDefaultAccount] = useState(null)
  const [quarterlyFinancialData, setQuarterlyFinancialData] = useState(null)

  // タイムレンジや期間、ラベル設定をローカルステートで管理
  const [selectedTimeRange, setSelectedTimeRange] = useState(null)
  const [selectedMarketTimeRange, setSelectedMarketTimeRange] = useState(null)
  const [selectedPeriod, setSelectedPeriod] = useState(null)
  const [labelState, setLabelState] = useState("auto")

  const tabContentRef = useRef(null)

  // 市場系のカラム（株価・出来高など）
  const marketColumns = ["stock_price", "market_value", "per", "pbr", "psr", "volume", "dividend_yield"]
  
  useEffect(() => {
    const getLatestStockData = async () => {
        // 昇順 
        let stockData;
        if (dashboardState.latestStockPriceData) {
            stockData = dashboardState.latestStockPriceData;
        } else {
            const dataNum = 2;
            const fetchedData = await fetchStockData(secCode, dataNum, navigate, location);
            stockData = fetchedData.data;
            dashboardDispatch({type: 'SET_LATEST_STOCK_PRICE_DATA', payload: stockData})
        }
        setLatestStockPrice(stockData);
    }

    const getFinancialData = async () => {
        // 降順 
        let finData;
        if (dashboardState.financialData) {
            finData = dashboardState.financialData;
        } else {
            const fetchedData = await fetchFinancialResults(secCode, navigate, location);
            finData = fetchedData.data;
            dashboardDispatch({type: 'SET_FINANCIAL_DATA', payload: finData})
        }
      
      if (finData.length > 0) {
        const latest = finData[finData.length - 1]
        setLatestFinancialData(latest);
        setFinancialData(finData); // 昇順で保存
      } else {
        setLatestFinancialData([]);
        setFinancialData([]); // 昇順で保存
      }
      
    }

    const getMetricsList = async () => {
        if (dashboardState.metricsList) {
            setMetricsList(dashboardState.metricsList)    
        } else {
            const fetchedData = await fetchMeticsList(navigate, location);
            setMetricsList(fetchedData.data)
            dashboardDispatch({type: 'SET_METRICS_LIST', payload: fetchedData.data})
        }
    }

    const getLatestDividendData = async () => {
        if (dashboardState.latestDividendData) {
            setLatestDividendData(dashboardState.latestDividendData)    
        } else {
            const fetchedData = await fetchLatestDividendData(secCode, navigate, location);
            setLatestDividendData(fetchedData.data)
            dashboardDispatch({ type: 'SET_LATEST_DIVIDEND_DATA', payload: fetchedData.data})
        }
    }

    // const getCompanyInfo = async () => {
    //     // 降順 
    //     let companyData;
    //     if (dashboardState.companyInfo) {
    //         companyData = dashboardState.companyInfo;
    //         // setCompanyInfo(companyData); // 昇順で保存
    //     } 
        
    //   }
      getMetricsList();
      // getCompanyInfo();
      getLatestStockData();
      getFinancialData();
      getLatestDividendData();
  }, [secCode, dashboardState.companyInfo])


  /**
   * 初回（またはクエリパラメータが変化した時）に、URL or ユーザー設定 からローカル state を反映
   *
   *  - column: 選択指標
   *  - year: タイムレンジ
   *  - annualQuarter: 年次 or 四半期
   *  - label: グラフラベル表示設定
   */
  useEffect(() => {
    const column = searchParams.get("column")
    const year = searchParams.get("year")
    const annualQuarter = searchParams.get("annualQuarter")

    // 1) 選択指標を確定
    if (selectableAccounts) {
      const userSettingColumn = getDescriptionBySettingName(userSettings, "dashboard_summary_graph_1")
      if (column) {
        // URL優先
        const accountDefinition = selectableAccounts.find((acc) => acc.col === column)
        setSelectedAccount(accountDefinition || null)
      } else if (userSettingColumn) {
        const accountDefinition = selectableAccounts.find((acc) => acc.col === userSettingColumn)
        setSelectedAccount(accountDefinition || null)
      } else {
        const col = selectableAccounts.find((acc) => acc.col === "stock_price");
        setSelectedAccount(col || null)
      }
    }

    // 2) タイムレンジを確定
    const userSettingTimeRange = getDescriptionBySettingName(userSettings, "dashboard_summary_graph_1_timerange")
    const userSettingMarketTimeRange = getDescriptionBySettingName(userSettings, "dashboard_summary_graph_1_market_timerange")
    if (year) {
      // URL優先
      setSelectedTimeRange(year)
      setSelectedMarketTimeRange(year)
    } else if (userSettingTimeRange) {
      setSelectedTimeRange(userSettingTimeRange)
      setSelectedMarketTimeRange(userSettingMarketTimeRange)
    } else {
      // デフォルト
      setSelectedTimeRange("5Y")
      // 市場系はユーザー設定がなければ "1Y" にしているが、ここでは同時に合わせておく or 必要に応じて分ける
      setSelectedMarketTimeRange("1Y")
    }

    // 3) 年次/四半期を確定
    const userSettingPeriod = getDescriptionBySettingName(userSettings, "dashboard_summary_graph_1_period")
    if (annualQuarter) {
      // URL優先
      setSelectedPeriod(annualQuarter)
    } else if (userSettingPeriod) {
      setSelectedPeriod(userSettingPeriod)
    } else {
      setSelectedPeriod("annual")
    }

    // 4) ラベル表示設定を確定
    const label = getDescriptionBySettingName(userSettings, "dashboard_summary_graph_1_label")
    setLabelState(label || "auto")
  }, [searchParams, userSettings, selectableAccounts, defaultAccount])

  /**
   * defaultAccount を初期化
   */
  useEffect(() => {
    if (selectableAccounts && selectableAccounts.length > 0) {
      setDefaultAccount(selectableAccounts.find((acc) => acc.col === "stock_price"))
    }
  }, [selectableAccounts])

  /**
   * レスポンシブ幅による2カラム or 1カラムの切り替え
   */
  useEffect(() => {
    const tabContent = tabContentRef.current
    if (!tabContent) return
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsTwoColumns(entry.contentRect.width >= 1000)
      }
    })

    resizeObserver.observe(tabContent)
    return () => {
      resizeObserver.unobserve(tabContent)
    }
  }, [tabContentRef.current, loaded])

  useEffect(() => {
    if (tabContentRef.current) {
      tabContentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [secCode, loaded, graphLoaded]);

  /**
   * quarter(四半期)データを取得
   */
  const getQuarterlyData = useCallback(async () => {
    if (selectedAccount?.col) {
      const quarterlyData = await fetchQuarterlyFinancialData(secCode, selectedAccount.col)
      setQuarterlyFinancialData(quarterlyData.data)
    }
  }, [selectedAccount, secCode])

  /**
   * annualQuarter(期間)の切り替えイベントハンドラ
   */
  const onPeriodChange = (period) => {
    setSelectedPeriod(period)
    if (period === "quarterly") {
      getQuarterlyData()
    }
  }

  /**
   * 配当データの取得
   */
  const getDividendData = useCallback(async () => {
    if (["dividend", "payout_ratio"].includes(selectedAccount?.col)) {
      let divData = dashboardState.dividendData
      if (!divData) {
        const fetchedData = await fetchDividendData(secCode, true, true)
        divData = fetchedData.data
        dashboardDispatch({ type: "SET_DIVIDEND_DATA", payload: divData })
      }
      setDividendData(divData)
    }
  }, [selectedAccount, dashboardState, dashboardDispatch, secCode])

  /**
   * 指標 or 期間が変わったら 配当 or 四半期データを必要に応じて取得
   */
  useEffect(() => {
    getDividendData()
    if (selectedPeriod === "quarterly") {
      getQuarterlyData()
    }
  }, [selectedPeriod, getDividendData, getQuarterlyData])

  /**
   * 「設定を保存」クリック時
   */
  const onGraphSaveClick = async () => {
    const newSettings = [
      {
        setting_name: "dashboard_summary_graph_1",
        description: selectedAccount?.col || "",
      },
      {
        // 市場系のカラムなら market_timerange を使い、そうでないなら timerange を使う
        setting_name: marketColumns.includes(selectedAccount?.col)
          ? "dashboard_summary_graph_1_market_timerange"
          : "dashboard_summary_graph_1_timerange",
        description: marketColumns.includes(selectedAccount?.col) ? selectedMarketTimeRange : selectedTimeRange,
      },
      {
        setting_name: "dashboard_summary_graph_1_period",
        description: selectedPeriod,
      },
      {
        setting_name: "dashboard_summary_graph_1_label",
        description: labelState,
      },
    ]

    for (const setting of newSettings) {
      await addUserSetting(setting)
    }
  }

  useEffect(() => {
    if (selectedAccount) {
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams)
        newParams.set("column", selectedAccount.col)
        return newParams
      })
    }
  }, [selectedAccount, setSearchParams])

  
  return (
    <div className="tabcontent" ref={tabContentRef}>
      {(!companyInfo || !latestStockPrice || !financialData) ? (
        <Loading />
      ): (
        <>
          <MainMetrics
            companyInfo={companyInfo}
            accountingStandard={accountingStandard}
            financialData={financialData}
            stockPriceData={latestStockPrice}
            metricsList={metricsList}
            setLoaded={setLoaded}
            stockCurrency={companyInfo.currency}
          />
          {companyInfo.company_description && <CompanyInformation description={companyInfo.company_description} />}
          <div className={`grid gap-1 md:gap-4 ${isTwoColumns ? "grid-cols-[5fr_3fr]" : "grid-cols-1"}`}>
            
              {selectedAccount && 
                <div className="dashboard-row h-[630px] max-h-screen ">
                  <div className="flex h-[45px] mb-5">
                    <SelectColumnContainer selectedAccount={selectedAccount} setSelectedAccount={setSelectedAccount} />
                    <SaveSettingButton onSaveClick={onGraphSaveClick} />
                  </div>
                  {marketColumns.includes(selectedAccount?.col) ? (
                    <SummaryMarketGraph
                      column={selectedAccount?.col}
                      stockCurrency={companyInfo.currency}
                      timeRange={selectedMarketTimeRange}
                      setTimeRange={setSelectedMarketTimeRange}
                      reportedCurrency={latestFinancialData?.reported_currency}
                      setLoaded={setLoaded}
                    />
                  ) : (
                    <SummaryGraph
                      financialData={financialData}
                      quarterlyFinancialData={quarterlyFinancialData}
                      dividendData={dividendData}
                      account={selectedAccount?.col}
                      accountName={selectedAccount?.col_name}
                      unit={selectedAccount?.unit}
                      onPeriodChange={onPeriodChange}
                      selectedTimeRange={selectedTimeRange}
                      setSelectedTimeRange={setSelectedTimeRange}
                      labelState={labelState}
                      setLabelState={setLabelState}
                      period={selectedPeriod}
                      setLoaded={setLoaded}
                    />
                  )}
                </ div>  
              }
            <FinancialDataSummary
              financialData={latestFinancialData}
              stockPriceData={latestStockPrice[1]}
              companyInfo={companyInfo}
              dividendData={latestDividendData}
              accountingStandard={accountingStandard}
              stockCurrency={companyInfo.currency}
              />
            </div>
        </>
      )}
    </div>
  )
}

export default SummaryTab
